import { useEffect, useState } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useMediaQuery } from '@hooks/useMediaQuery'
import { cx } from '@utils/cx'
import { getAvatarSrc } from '../logic/getAvatarSrc'
import styles from './Podium.module.css'

export default function Podium ({ players }) {
  const [visibleRanks, setVisibleRanks] = useState([false, false, false])
  const [readRankIndex, setReadRankIndex] = useState(null)
  const { t } = useLanguage()
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  // Progressive animation: third → second → first place
  useEffect(() => {
    const timers = [
      setTimeout(() => setVisibleRanks([true, false, false]), 500),
      setTimeout(() => setVisibleRanks([true, true, false]), 2500),
      setTimeout(() => setVisibleRanks([true, true, true]), 4500),

      setTimeout(() => setReadRankIndex(0), 500), // bronze
      setTimeout(() => setReadRankIndex(1), 2500), // silver
      setTimeout(() => setReadRankIndex(2), 4500) // gold
    ]
    return () => timers.forEach(clearTimeout)
  }, [])

  const rankStyles = [styles.third, styles.second, styles.first]

  const getBlockClasses = (rankIndex) => {
    const isVisible = visibleRanks[rankIndex] || reduceMotion
    return cx(
      styles.block,
      rankStyles[rankIndex],
      isVisible && styles.blockVisible,
      reduceMotion && styles.blockStatic
    )
  }

  const getContentClasses = () => {
    return cx(
      styles.content,
      reduceMotion && styles.contentStatic
    )
  }

  return (
    <>
      <div className={styles.podium}>
        {/* Second place (left) */}
        <div className={getBlockClasses(1)}>
          {players[1] && (
            <div className={getContentClasses()}>
              <img
                src={getAvatarSrc(players[1].avatar)}
                alt={t.shared.access.player.avatarAlt.replace('{name}', players[1].name)}
                className={styles.avatar}
              />
              <p className={styles.name}>{players[1].name}</p>
              <span
                className={styles.points}
                aria-label={`${players[1].score} ${t.shared.access.player.points}`}
              >
                {players[1].score} pts
              </span>
            </div>
          )}
        </div>

        {/* First place (centre) */}
        <div className={getBlockClasses(2)}>
          {players[0] && (
            <div className={getContentClasses()}>
              <img
                src={getAvatarSrc(players[0].avatar)}
                alt={t.shared.access.player.avatarAlt.replace('{name}', players[0].name)}
                className={styles.avatar}
              />
              <p className={styles.name}>{players[0].name}</p>
              <span
                className={styles.points}
                aria-label={`${players[0].score} ${t.shared.access.player.points}`}
              >
                {players[0].score} pts
              </span>
            </div>
          )}
        </div>

        {/* Third place (right) */}
        <div className={getBlockClasses(0)}>
          {players[2] && (
            <div className={getContentClasses()}>
              <img
                src={getAvatarSrc(players[2].avatar)}
                alt={t.shared.access.player.avatarAlt.replace('{name}', players[2].name)}
                className={styles.avatar}
              />
              <p className={styles.name}>{players[2].name}</p>
              <span
                className={styles.points}
                aria-label={`${players[2].score} ${t.shared.access.player.points}`}
              >
                {players[2].score} pts
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Synchronized screen reading */}
      {readRankIndex !== null &&
      players.length > readRankIndex &&
      players[players.length - 1 - readRankIndex] &&
      (
        <p className='sr-only' aria-live='polite'>
          {(() => {
            const index = players.length - 1 - readRankIndex // Reverse index for screen reader
            const player = players[index]
            const rank = readRankIndex + 1
            const label =
              rank === 1
                ? t.shared.podium.third
                : rank === 2
                  ? t.shared.podium.second
                  : rank === 3
                    ? t.shared.podium.first
                    : t.shared.podium.generic.replace('{rank}', rank)
            return `${label}: ${player.name}, ${player.score} ${t.shared.access.player.points}`
          })()}
        </p>
      )}

    </>
  )
}
